import {
  AGENT_LABEL,
  AGENT_PAID_MESSAGE,
  AGENT_NAME,
  BOQ_ACCEPT,
  BOQ_MAX_BYTES,
  timeAgo,
  type InboxMail,
  type InboxOption,
  t,
  translateLoose,
} from '@procumi/shared';
import { useRef, useState } from 'react';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { boqFromExtraction, setBoqState } from '../lib/boq';
import { copyText } from '../lib/clipboard';
import { isBuild } from '../lib/terms';
import type { Route } from '../routes';
import { rich } from '../lib/rich';

/**
 * Agentul de achiziții (2026-10-28), în două părți:
 * - „Spune-i ce-ți trebuie”: un mesaj scris în grabă, o poză cu o listă, un Excel.
 *   Agentul scoate pozițiile pe categorii (aceeași citire ca la antemăsurătoare),
 *   apoi omul face cererile, cu furnizorii categoriei deja bifați;
 * - căsuța: ofertele venite pe mail, puse singure la furnizor pe cerere și citite.
 *   Omul le verifică și le salvează; ce nu s-a putut potrivi, alege el unde merge.
 * Agentul nu trimite și nu salvează nimic singur.
 */

const STARE: Record<InboxMail['status'], { cls: string; text: string }> = {
  ready: { cls: 'act', text: t('De verificat') },
  unmatched: { cls: 'wait', text: t('De potrivit') },
  done: { cls: 'ok', text: t('Salvată') },
  ignored: { cls: 'off', text: t('Pusă deoparte') },
};

export function AgentPage({
  go,
  toast,
  canExtract,
}: {
  go: (r: Route) => void;
  toast: Toast;
  canExtract: boolean;
}) {
  const inbox = useAsync(() => api.inbox(), [], 'inbox');
  const [text, setText] = useState('');
  const [reading, setReading] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const ask = async (src: { file: File } | { text: string }, name: string) => {
    if ('file' in src && src.file.size > BOQ_MAX_BYTES) {
      toast(t('Fișierul e prea mare. Maximul e {0} MB.', Math.round(BOQ_MAX_BYTES / (1024 * 1024))));
      return;
    }
    setReading(name);
    try {
      const out = await api.extractBoq(src);
      if (!out.lines.length) {
        toast(t('N-am găsit ce trebuie cumpărat. Scrie produsele și cantitățile.'));
        return;
      }
      setBoqState(boqFromExtraction(out, name));
      go({ view: 'boq' });
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setReading(null);
    }
  };

  const example = isBuild()
    ? t(
        'ex. Avem nevoie pe șantierul din Cluj de 40 mc beton C25/30, 3 t oțel BST500 Ø12 și 200 saci adeziv, până vineri.',
      )
    : t(
        'ex. Pentru restaurant: 20 l ulei floarea-soarelui, 50 kg făină 000, 30 bax apă plată 0,5 l, până joi.',
      );

  return (
    <>
      <div className="head">
        <div>
          <h1>{AGENT_LABEL}</h1>
          <p>
            {t(
              'Îi spui ce-ți trebuie, el face cererile pe categorii. Ofertele venite pe mail le pune singur la cererea lor și citește prețurile. Tu verifici și decizi.',
            )}
          </p>
        </div>
      </div>

      {inbox.data && !inbox.data.allowed ? (
        <section className="sheet ag-paid">
          <h2>{t('{0} lucrează pentru firmele cu pachet', AGENT_NAME)}</h2>
          <ul>
            <li>
              {t(
                'Îi scrii ce-ți trebuie, ca pe WhatsApp, sau îi dai o poză cu lista: face cererile pe categorii.',
              )}
            </li>
            <li>{t('Ofertele venite pe mail le pune singur la cererea lor și citește prețurile.')}</li>
            <li>{t('Tu verifici și decizi: nu trimite și nu salvează nimic fără tine.')}</li>
          </ul>
          <p>{t(AGENT_PAID_MESSAGE)}</p>
          <button className="btn primary" onClick={() => go({ view: 'company', tab: 'plan' })}>
            {t('Alege pachetul')}
          </button>
        </section>
      ) : null}
      {!inbox.data?.allowed ? null : (
        <section className="sheet ag-ask" aria-busy={!!reading}>
          <h2>{t('Spune-i ce-ți trebuie')}</h2>
          {reading ? (
            <div className="ag-reading" aria-live="polite">
              <span className="boq-spin" aria-hidden="true" />
              <p>{t('Citesc „{0}” și împart pozițiile pe categorii…', reading)}</p>
            </div>
          ) : (
            <>
              <label className="f" htmlFor="ag-text">
                {t('Scrie ca pe WhatsApp sau lipește o listă')}
                <textarea
                  id="ag-text"
                  className="inp"
                  rows={5}
                  value={text}
                  placeholder={t(example)}
                  onChange={(e) => setText(e.target.value)}
                />
              </label>
              <div className="ag-acts">
                <button
                  className="btn primary"
                  disabled={!text.trim() || !canExtract}
                  onClick={() => void ask({ text: text.trim() }, t('mesajul tău'))}
                >
                  {t('Fă cererile')}
                </button>
                <button className="btn" disabled={!canExtract} onClick={() => input.current?.click()}>
                  {t('Încarcă o poză, un PDF sau un Excel')}
                </button>
                <input
                  ref={input}
                  type="file"
                  hidden
                  accept={BOQ_ACCEPT}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void ask({ file: f }, f.name);
                  }}
                />
              </div>
              {canExtract ? null : <p className="ag-note">{t('Citirea automată nu e pornită pe server.')}</p>}
            </>
          )}
        </section>
      )}

      {inbox.data && !inbox.data.allowed ? null : inbox.loading ? (
        <Loading />
      ) : inbox.error || !inbox.data ? (
        <ErrorBox error={inbox.error ?? t('Nu am putut încărca.')} onRetry={inbox.reload} />
      ) : (
        <section className="sheet ag-inbox">
          <div className="ag-inbox-head">
            <div>
              <h2>{t('Ofertele venite pe mail')}</h2>
              {inbox.data.address ? (
                <p>
                  {rich(
                    'Răspunsurile la cererile trimise din Procumi ajung aici singure. O ofertă venită la tine în căsuță: dă-i <b>Forward</b> la adresa lui {0}.',
                    AGENT_NAME,
                  )}
                </p>
              ) : (
                <p>{t('Căsuța lui {0} nu e pornită încă pe server.', AGENT_NAME)}</p>
              )}
            </div>
            {inbox.data.address ? (
              <div className="ag-address">
                <code>{inbox.data.address}</code>
                <button className="btn small" onClick={() => void copyText(inbox.data!.address!, toast)}>
                  {t('Copiază adresa')}
                </button>
              </div>
            ) : null}
          </div>
          {inbox.data.mails.length ? (
            <ul className="ag-list">
              {inbox.data.mails.map((m) => (
                <MailRow
                  key={m.id}
                  mail={m}
                  go={go}
                  toast={toast}
                  onChange={(next) =>
                    inbox.setData({
                      ...inbox.data!,
                      mails: inbox.data!.mails.map((x) => (x.id === next.id ? next : x)),
                    })
                  }
                />
              ))}
            </ul>
          ) : (
            <p className="ag-empty">
              {t(
                'Niciun mail încă. Când un furnizor răspunde, oferta apare aici, citită, gata de verificat.',
              )}
            </p>
          )}
        </section>
      )}
    </>
  );
}

function MailRow({
  mail: m,
  go,
  toast,
  onChange,
}: {
  mail: InboxMail;
  go: (r: Route) => void;
  toast: Toast;
  onChange: (m: InboxMail) => void;
}) {
  const [options, setOptions] = useState<InboxOption[] | null>(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const st = STARE[m.status];

  const loadOptions = async () => {
    setBusy(true);
    try {
      setOptions(await api.inboxOptions(m.id));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const next = await api.inboxAssign(m.id, pick);
      onChange(next);
      setOptions(null);
      toast(
        next.readCount
          ? t('Pus la cerere. {0} a citit {1} poziții: verifică oferta.', AGENT_NAME, next.readCount)
          : t('Pus la cerere.'),
      );
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const ignore = async (back: boolean) => {
    setBusy(true);
    try {
      onChange(await api.inboxIgnore(m.id, back));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const from =
    m.senderEmail !== m.fromEmail
      ? t('{0} · Forward de la {1}', m.senderEmail, m.fromEmail)
      : m.fromName || m.fromEmail;

  return (
    <li className={`ag-row ${m.status}`}>
      <div className="ag-row-top">
        <span className={`pill ${st.cls}`}>{st.text}</span>
        <b>{m.supplier?.name ?? from}</b>
        {m.rfq ? <span className="ag-rfq">„{m.rfq.title}”</span> : null}
        <small>{timeAgo(m.receivedAt)}</small>
      </div>
      <p className="ag-subject">{m.subject || t('(fără subiect)')}</p>
      {m.excerpt ? <p className="ag-excerpt">{m.excerpt}</p> : null}
      {m.files.length ? (
        <p className="ag-files">
          {m.files.map((f) => (
            <a key={f.index} href={api.inboxFileUrl(m.id, f.index)}>
              {f.filename}
            </a>
          ))}
        </p>
      ) : null}
      {m.note ? <p className="ag-note">{translateLoose(m.note)}</p> : null}
      {m.status === 'ready' && m.readCount ? (
        <p className="ag-read">
          {t(
            '{0} a citit {1} {2}.',
            AGENT_NAME,
            m.readCount,
            m.readCount === 1 ? t('poziție') : t('poziții'),
          )}
        </p>
      ) : null}

      {options ? (
        <div className="ag-pick">
          {options.length ? (
            <>
              <select
                className="inp"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                aria-label={t('Cererea și furnizorul')}
              >
                <option value="">{t('Alege cererea și furnizorul…')}</option>
                {options.map((o) => (
                  <optgroup key={o.rfqId} label={o.title}>
                    {o.sends.map((s) => (
                      <option key={s.sendId} value={s.sendId}>
                        {s.supplier}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <button className="btn primary small" disabled={!pick || busy} onClick={() => void assign()}>
                {t('Pune la cerere')}
              </button>
            </>
          ) : (
            <span className="ag-note">
              {t(
                'Nu e nicio cerere deschisă {0}. Adaugă furnizorul la cerere, apoi încearcă din nou.',
                m.supplier ? t('trimisă acestui furnizor') : '',
              )}
            </span>
          )}
          <button className="btn small" onClick={() => setOptions(null)}>
            {t('Renunță')}
          </button>
        </div>
      ) : (
        <div className="ag-acts">
          {m.status === 'ready' && m.rfq ? (
            <button
              className="btn primary small"
              onClick={() => go({ view: 'rfq', id: m.rfq!.id, inbox: m.id })}
            >
              {t('Verifică oferta')}
            </button>
          ) : null}
          {m.status === 'done' && m.rfq ? (
            <button className="btn small" onClick={() => go({ view: 'rfq', id: m.rfq!.id })}>
              {t('Deschide cererea')}
            </button>
          ) : null}
          {m.status === 'unmatched' || m.status === 'ready' ? (
            <button className="btn small" disabled={busy} onClick={() => void loadOptions()}>
              {m.status === 'unmatched' ? t('Alege cererea') : t('Altă cerere')}
            </button>
          ) : null}
          {m.status === 'ignored' ? (
            <button className="btn small" disabled={busy} onClick={() => void ignore(true)}>
              {t('Adu înapoi')}
            </button>
          ) : m.status !== 'done' ? (
            <button className="btn small" disabled={busy} onClick={() => void ignore(false)}>
              {t('Nu e ofertă')}
            </button>
          ) : null}
        </div>
      )}
    </li>
  );
}
